import { useMutation } from '@w/react-query'
import type { Flashcard } from '../db/schema'
import { useRouterContext } from '../router/flashcards-fe.router-bindings'
import { actOnCard } from '../db/learner-actions'
import { writeWorkspace } from '../db/workspace-write'
import type { SpaceResource } from '../router/spaces-resources'

type Field = 'importance' | 'difficulty'

/** The Current tab of a question's settings popover: the viewer's own card values for one qid (editor-home spec,
 * Default and actual controls). Without a card yet there is nothing of the viewer's to change. */
export function QuestionCurrentSettings({ qid, cards, resource }: { qid: string; cards: Flashcard[]; resource: SpaceResource }) {
  const ctx = useRouterContext()
  const card = cards.find(card => card.qid === qid && card.userId === resource.userId)
  const mutation = useMutation({ mutationFn: (input: { card: Flashcard; field: Field; value: string }) => {
    const patch = { [input.field]: input.value as 'low' | 'medium' | 'high' }
    return Promise.resolve(writeWorkspace(ctx, resource.space.workspaceId, () => actOnCard(resource.db, {
      userId: resource.userId, deviceId: ctx.deviceId, qid: input.card.qid, action: { kind: 'rated', patch },
    })))
  } })
  if (!card) return <p className="text-on-surface-secondary text-sm">No card of yours yet.</p>
  if (card.deletedAt !== null) return <p role="status">Card unavailable</p>
  return <div className="flex flex-wrap items-end gap-5 pt-3">
    {(['importance', 'difficulty'] as const).map(field => <label key={field} className="flex flex-col gap-1 text-xs text-on-surface-secondary capitalize">{field}
      <select data-testid={`question-actual-${field}-${qid}`} value={card[field]} disabled={mutation.isPending} className="text-on-surface text-sm"
        onChange={event => { mutation.mutate({ card, field, value: event.target.value }) }}>
        {['low', 'medium', 'high'].map(value => <option key={value} value={value}>{value}</option>)}
      </select>
    </label>)}
    {mutation.error ? <p role="alert">{mutation.error.message}</p> : null}
  </div>
}
